import { codeSeries, mapEtapaToGradeName, mapEtapaToSegment } from "@/lib/utils";

/** Produto de um seletor agrupado por segmento e série (mesmo agrupamento do seletor de itens do cupom). */
export interface ProductPickerRow {
  productId: number;
  productSeries: string | null;
  description: string;
  /** Código do produto (CXC). */
  code: string;
  sku: string;
  /** Tipo do produto, pela descrição (Aluno, Professor…). */
  usage: string;
}

export interface ProductPickerGroup<T extends ProductPickerRow = ProductPickerRow> {
  key: string;
  items: T[];
}

export interface ProductPickerSeries<T extends ProductPickerRow = ProductPickerRow> {
  key: string;
  name: string;
  items: T[];
}

export const normalizePickerText = (text: string) =>
  text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

/** Busca por descrição, código CXC ou SKU, sem diferenciar acento nem maiúscula. */
export const matchesProductRow = (row: ProductPickerRow, query: string) => {
  const term = normalizePickerText(query);
  if (!term) return true;
  return [row.description, row.code, row.sku].some((value) => !!value && normalizePickerText(value).includes(term));
};

/** Agrupa por segmento (Educação Infantil, Fundamental I…) na ordem escolar; "Outros" fica no fim. */
export const groupProductRows = <T extends ProductPickerRow>(rows: T[]): ProductPickerGroup<T>[] => {
  const groups = new Map<string, ProductPickerGroup<T>>();
  for (const row of rows) {
    const key = mapEtapaToSegment(row.productSeries);
    const group = groups.get(key);
    if (group) group.items.push(row);
    else groups.set(key, { key, items: [row] });
  }
  const order = (group: ProductPickerGroup<T>) =>
    Math.min(...group.items.map((row) => codeSeries(mapEtapaToGradeName(row.productSeries))));
  for (const group of groups.values()) {
    group.items.sort(
      (a, b) =>
        codeSeries(mapEtapaToGradeName(a.productSeries)) - codeSeries(mapEtapaToGradeName(b.productSeries))
        || a.description.localeCompare(b.description, "pt-BR"),
    );
  }
  return Array.from(groups.values()).sort((a, b) => order(a) - order(b));
};

export const pickerSeriesKey = (segment: string, series: string) => `${segment}::${series}`;

/** Séries dentro de um segmento, na ordem escolar (os itens do segmento já vêm ordenados por série). */
export const seriesOfGroup = <T extends ProductPickerRow>(group: ProductPickerGroup<T>): ProductPickerSeries<T>[] => {
  const blocks = new Map<string, ProductPickerSeries<T>>();
  for (const row of group.items) {
    const name = mapEtapaToGradeName(row.productSeries);
    const block = blocks.get(name);
    if (block) block.items.push(row);
    else blocks.set(name, { key: pickerSeriesKey(group.key, name), name, items: [row] });
  }
  return Array.from(blocks.values());
};

/** Chaves (segmentos e séries) dos grupos visíveis, para abrir tudo o que a busca encontrou. */
export const allPickerKeys = <T extends ProductPickerRow>(groups: ProductPickerGroup<T>[]): string[] =>
  groups.flatMap((group) => [group.key, ...seriesOfGroup(group).map((block) => block.key)]);

export const filterProductGroups = <T extends ProductPickerRow>(
  groups: ProductPickerGroup<T>[],
  query: string,
): ProductPickerGroup<T>[] => {
  if (!query.trim()) return groups;
  return groups
    .map((group) => ({ ...group, items: group.items.filter((row) => matchesProductRow(row, query)) }))
    .filter((group) => group.items.length > 0);
};
